import React from 'react';
import { determineMaxSavings } from '@/components/account/NextPlayKit/utils';
import { constructLegacyCustomerPortalLink } from '@/utils/shopSubdomain';
import {
  buildBannerPropsWithDynamicContent,
  trackClickEvent,
  TypeScheduledItemGroup,
} from '@/components/account/PlayKitSchedule/utils';
import { TypePlayKitBundleOffer } from '@/lib/api/productCatalog/types';
import { TypeBanner } from '@/core/generated-types';
import useFeature from '@/hooks/useFeature';
import useLocale from '@/hooks/useLocale';
import { CmsBanner } from '@/components/generics/GenBanner';
import {
  CTA_NAMES,
  EXTENSION_UPSELL_EVENT_STEPS,
  trackExtensionFlowStepEvent,
} from '@/utils/rudderstack';
import { getPlatform } from '@/utils/helpers';

export type PrepayUpsellBannersProps = {
  group: TypeScheduledItemGroup;
  prepayBundleExtensionOffers: TypePlayKitBundleOffer[];
  prepayBanner?: TypeBanner;
  holidayBanner?: TypeBanner;
};

function PrepayUpsellBanners({
  group,
  prepayBundleExtensionOffers,
  prepayBanner,
  holidayBanner,
}: PrepayUpsellBannersProps) {
  const isBfcmEnabled = useFeature('money_bfcm-mode-2025');
  const locale = useLocale();

  const groupHasNextItemEligibleForPrepayExtension = group.items.some(
    (item) => item.isNextItemEligibleForPrepayExtension,
  );

  const isEligibleForPrepayUpsell =
    !isBfcmEnabled &&
    !group.isPartialSub &&
    groupHasNextItemEligibleForPrepayExtension &&
    prepayBundleExtensionOffers.length > 0;
  const isEligibleForHolidayUpsell =
    isBfcmEnabled &&
    !group.isPartialSub &&
    groupHasNextItemEligibleForPrepayExtension &&
    prepayBundleExtensionOffers.length > 0;

  if (!isEligibleForPrepayUpsell && !isEligibleForHolidayUpsell) {
    return null;
  }

  const maxSavings = determineMaxSavings(prepayBundleExtensionOffers);
  const prepayExtensionFlowLink = constructLegacyCustomerPortalLink(locale, [
    group.userUuid,
    'subscriptions',
    group.subscriptionUuid,
    'prepay-extension',
  ]);
  const prepayAndSaveBannerProps =
    prepayBanner &&
    buildBannerPropsWithDynamicContent(prepayBanner.fields, {
      linkUrl: prepayExtensionFlowLink,
      headingStringInterpolations: { savings: maxSavings },
    });

  const holidayBannerProps =
    holidayBanner &&
    buildBannerPropsWithDynamicContent(holidayBanner.fields, {
      linkUrl: prepayExtensionFlowLink,
    });

  const clickEvents = () => {
    trackExtensionFlowStepEvent({
      stepName: EXTENSION_UPSELL_EVENT_STEPS.STARTED.name,
      url: window.location.href,
      bundleSize: '',
      platform: getPlatform(),
      stepNumber: EXTENSION_UPSELL_EVENT_STEPS.STARTED.stepNumber,
    });
    trackClickEvent(CTA_NAMES.EXTENSION_FLOW_STARTED);
  };

  return (
    <>
      {isEligibleForPrepayUpsell && prepayAndSaveBannerProps && (
        <CmsBanner
          testId="prepay-extension-banner"
          {...prepayAndSaveBannerProps}
          classes="mb-6"
          onClickCta={() => clickEvents()}
        />
      )}
      {isEligibleForHolidayUpsell && holidayBannerProps && (
        <CmsBanner
          testId="prepay-extension-holiday-banner"
          {...holidayBannerProps}
          classes="mb-6"
          onClickCta={() => clickEvents()}
        />
      )}
    </>
  );
}

export default PrepayUpsellBanners;
